// Spec: docs/plans/frontend-stack.md §3.1 (selection priority).
//
// Skin selection priority (issue #612 / D-Skins-2):
//   1. ?skin= query param  (one-off preview; writes through to localStorage)
//   2. localStorage         (last explicit local choice)
//   3. users.skin (server)  (persisted per-user preference, async-hydrated)
//   4. 'modern' default
//
// This module is SHARED skin infra (apps/web/src/skin-loader.tsx mounts it)
// and must NOT import any individual skin's code. It uses a bare
// same-origin fetch with the VITE_API_BASE_URL convention rather than the
// skin-local api wrapper, to stay skin-agnostic.

import { useEffect, useState } from 'react';

const VALID_SKINS = ['modern', 'legacy', 'deck'] as const;
export type SkinName = (typeof VALID_SKINS)[number];

const STORAGE_KEY = 'aw.skin';
const DEFAULT_SKIN: SkinName = 'modern';

// Same-origin API base, matching the skin-local api wrappers.
const API_BASE_URL = (
  import.meta.env.VITE_API_BASE_URL ?? '/api'
).replace(/\/$/, '');

function isSkinName(v: unknown): v is SkinName {
  return typeof v === 'string' && (VALID_SKINS as readonly string[]).includes(v);
}

/**
 * Persist an explicit local skin choice and switch the live skin in THIS
 * tab. Skin-agnostic: any skin's Settings selector calls this after the
 * server-side persistence succeeds. Other tabs pick it up via `storage`.
 * (Server persistence goes through the API — see the per-skin selector.)
 *
 * Navigation note (issue #618): each skin owns its own TanStack Router with
 * its own route tree. A skin switch is a full bundle swap — the incoming
 * skin's router mounts fresh and must start at a URL it knows. We navigate
 * to '/' so both skins' index→/home (or /dashboard) redirect runs cleanly
 * rather than leaving the browser at a path that may not exist in the target
 * skin's route tree (which would render nothing — a blank page). Using
 * window.location.replace avoids leaving a history entry for the mid-switch
 * URL.
 */
export function setSkinPreference(skin: SkinName): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY, skin);
  // Navigate to '/' so the new skin's router always starts at a route it
  // owns. Both skin routers define '/' (redirects to their home/dashboard).
  // Reloading here also writes the localStorage value durably before the
  // new skin's useSkin() reads it on the fresh page load.
  window.location.replace('/');
}

/** True when an explicit local choice (query or localStorage) is present. */
function hasExplicitLocalChoice(): boolean {
  if (typeof window === 'undefined') return false;
  const fromQuery = new URLSearchParams(window.location.search).get('skin');
  if (isSkinName(fromQuery)) return true;
  return isSkinName(window.localStorage.getItem(STORAGE_KEY));
}

function readPreference(): SkinName {
  if (typeof window === 'undefined') return DEFAULT_SKIN;

  // Priority 1: ?skin= query param (writes through to localStorage).
  const fromQuery = new URLSearchParams(window.location.search).get('skin');
  if (isSkinName(fromQuery)) {
    window.localStorage.setItem(STORAGE_KEY, fromQuery);
    return fromQuery;
  }

  // Priority 2: localStorage.
  const fromStorage = window.localStorage.getItem(STORAGE_KEY);
  if (isSkinName(fromStorage)) return fromStorage;

  // Priority 3 is async (see useSkin effect); fall through to default for
  // the synchronous first paint.
  return DEFAULT_SKIN;
}

/**
 * Priority 3: hydrate the persisted users.skin from /api/auth/me.
 * Returns the persisted skin, or null when unauthenticated / unavailable /
 * not a valid skin. Never throws.
 */
async function fetchPersistedSkin(
  signal: AbortSignal,
): Promise<SkinName | null> {
  if (typeof window === 'undefined') return null;
  try {
    const res = await fetch(`${API_BASE_URL}/auth/me`, {
      credentials: 'include',
      headers: { accept: 'application/json' },
      signal,
    });
    if (!res.ok) return null; // 401 (unauthenticated) and friends → fallback.
    const body = (await res.json()) as { skin?: unknown };
    return isSkinName(body.skin) ? body.skin : null;
  } catch {
    return null; // network error / abort → fallback.
  }
}

export function useSkin(): SkinName {
  const [skin, setSkin] = useState<SkinName>(readPreference);

  useEffect(() => {
    // The `storage` event fires in OTHER tabs when localStorage changes
    // (setSkinPreference writes to localStorage then does a full-page
    // navigation in the originating tab, so that tab reloads fresh).
    // Other tabs still need to react to the storage event to switch live.
    function onStorage(e: StorageEvent) {
      if (e.key === STORAGE_KEY) setSkin(readPreference());
    }
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  useEffect(() => {
    // Priority 3: only hydrate the server preference when the user has NOT
    // made an explicit local choice (query/localStorage). This preserves
    // Priority 1 + Priority 2 and avoids a flash for users with an explicit
    // ?skin= or localStorage choice.
    if (hasExplicitLocalChoice()) return;

    const controller = new AbortController();
    let cancelled = false;
    void fetchPersistedSkin(controller.signal).then((persisted) => {
      if (cancelled || persisted === null) return;
      // Re-check: a query/localStorage choice may have appeared while the
      // request was in flight — it wins over the server preference.
      if (hasExplicitLocalChoice()) return;
      setSkin(persisted);
    });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  return skin;
}
